import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { getFilters, createFilter, deleteFilter } from "../api.js";
import type { KeywordFilter } from "@tuit/shared-types";
import styles from "../styles/pages/SettingsPage.module.css";

export function FiltersPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { status: billing } = useBillingStatus();
  const [filters, setFilters] = useState<KeywordFilter[]>([]);
  const [keyword, setKeyword] = useState("");
  const [type, setType] = useState<"block" | "highlight">("block");
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    getFilters().then(setFilters).catch(() => {});
  }, [isSignedIn]);

  if (!isLoaded || !isSignedIn) return null;

  const canFilters = billing?.capabilities.canKeywordFilters ?? false;

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!keyword.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const f = await createFilter({ keyword: keyword.trim(), type });
      setFilters((prev) => [...prev, f]);
      setKeyword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add filter");
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await deleteFilter(id);
      setFilters((prev) => prev.filter((f) => f.id !== id));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
      </nav>
      <div className={styles.inner}>
        <h1 className={styles.heading}>Keyword Filters</h1>
        <p className={styles.sub}>Block or highlight posts matching specific keywords across all your feeds.</p>

        {!canFilters ? (
          <div className={styles.upsell}>
            <p>Keyword filters are available on <strong>Pro</strong> plan.</p>
            <Link to="/premium" className={styles.upsellLink}>Upgrade now →</Link>
          </div>
        ) : (
          <>
            <form className={styles.card} onSubmit={handleAdd}>
              <div className={styles.inlineFields}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Keyword or phrase"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
                <select className={styles.inputNarrow} value={type} onChange={(e) => setType(e.target.value as "block" | "highlight")}>
                  <option value="block">Block</option>
                  <option value="highlight">Highlight</option>
                </select>
                <button type="submit" className={styles.primaryBtn} disabled={adding || !keyword.trim()}>
                  {adding ? "Adding…" : "Add"}
                </button>
              </div>
              {error && <p className={styles.error}>{error}</p>}
            </form>

            {filters.length > 0 && (
              <ul className={styles.list}>
                {filters.map((f) => (
                  <li key={f.id} className={styles.listItem}>
                    <span className={`${styles.tag} ${f.type === "block" ? styles.tagBlock : styles.tagHighlight}`}>
                      {f.type}
                    </span>
                    <span className={styles.listItemText}>{f.keyword}</span>
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(f.id)}
                      disabled={deletingId === f.id}
                    >
                      {deletingId === f.id ? "…" : "Remove"}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {filters.length === 0 && <p className={styles.hint}>No filters yet.</p>}
          </>
        )}
      </div>
    </div>
  );
}
